import React, { useState } from 'react';
import { Shield, Server, Globe, CheckCircle, AlertTriangle, Copy, ExternalLink } from 'lucide-react';
import { Button } from './Button';

export const LocalDevSetup: React.FC = () => {
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);

  const copyToClipboard = (text: string, command: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCommand(command);
    setTimeout(() => setCopiedCommand(null), 2000);
  };

  const solutions = [
    {
      title: "1. Set API Base URL",
      description: "Configure your backend API endpoint",
      icon: Globe,
      color: "from-green-500 to-green-600",
      steps: [
        {
          title: "Create .env file",
          command: "cp .env.example .env",
          description: "Copy the example environment file"
        },
        {
          title: "Set API URL",
          command: "VITE_INVENTORY_API_URL=https://inventory-api.windingriverholdings.com\nVITE_AI_API_URL=https://ai-api.windingriversoftware.com",
          description: "Set your backend API URLs in the .env file"
        },
        {
          title: "For local development",
          command: "VITE_INVENTORY_API_URL=http://localhost:8000\nVITE_AI_API_URL=http://localhost:8001",
          description: "Use localhost for local backend development"
        },
        {
          title: "Restart dev server",
          command: "npm run dev",
          description: "Restart the development server to apply changes"
        }
      ]
    },
    {
      title: "2. Configure CORS",
      description: "Ensure your backend allows frontend requests",
      icon: Server,
      color: "from-blue-500 to-blue-600",
      steps: [
        {
          title: "Allow frontend origin",
          command: "# In your backend CORS configuration\nallow_origins = [\n    \"http://localhost:5173\",  # Development\n    \"https://yourdomain.com\",  # Production\n]",
          description: "Add your frontend URLs to allowed origins"
        },
        {
          title: "Allow credentials",
          command: "allow_credentials = true",
          description: "Enable credentials if using authentication"
        },
        {
          title: "Allow headers",
          command: "allow_headers = [\"*\"]",
          description: "Allow necessary headers for API requests"
        }
      ]
    },
    {
      title: "3. Test API Connection",
      description: "Verify your API is accessible",
      icon: Shield,
      color: "from-purple-500 to-purple-600",
      steps: [
        {
          title: "Test status endpoint",
          command: "curl https://inventory-api.windingriverholdings.com/api/health\ncurl https://ai-api.windingriversoftware.com/api/health",
          description: "Check if your API status endpoints respond"
        },
        {
          title: "Test in browser",
          command: "# Open browser developer tools\n# Check Network tab for API requests\n# Look for CORS or 404 errors",
          description: "Use browser dev tools to debug API calls"
        },
        {
          title: "Use System Status page",
          command: "# Click 'System Status' in the app\n# Run API connection tests",
          description: "Use the built-in API testing tools"
        }
      ]
    }
  ];

  return (
    <div className="space-y-8">
      {/* Problem Explanation */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <AlertTriangle className="w-6 h-6 text-yellow-600" />
          <h3 className="text-lg font-semibold text-yellow-800">API Configuration Required</h3>
        </div>
        <p className="text-yellow-700 mb-4">
          This frontend application needs to be configured to connect to your backend API service. 
          Here are the steps to configure the API connection:
        </p>
        <div className="bg-yellow-100 rounded p-3">
          <p className="text-sm text-yellow-800">
            <strong>Current Issue:</strong> API base URL is not configured or backend is not accessible.
          </p>
        </div>
      </div>

      {/* Solutions */}
      {solutions.map((solution, index) => (
        <div key={index} className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          <div className={`h-2 bg-gradient-to-r ${solution.color}`}></div>
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className={`p-2 rounded-lg bg-gradient-to-r ${solution.color}`}>
                <solution.icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-navy-900">{solution.title}</h3>
                <p className="text-navy-600">{solution.description}</p>
              </div>
            </div>

            <div className="space-y-4">
              {solution.steps.map((step, stepIndex) => (
                <div key={stepIndex} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-navy-900">{step.title}</h4>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(step.command, `${index}-${stepIndex}`)}
                      icon={Copy}
                      className="text-xs"
                    >
                      {copiedCommand === `${index}-${stepIndex}` ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                  <p className="text-sm text-navy-600 mb-3">{step.description}</p>
                  <pre className="bg-navy-50 p-3 rounded text-xs overflow-x-auto">
                    <code>{step.command}</code>
                  </pre>
                </div>
              ))}
            </div>

            {index === 0 && (
              <div className="mt-4 bg-green-50 border border-green-200 rounded p-3">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-800">Recommended Solution</span>
                </div>
                <p className="text-xs text-green-700">
                  Setting the API base URL is the most straightforward approach. Make sure your backend API is running and accessible.
                </p>
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Quick Links */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-navy-900 mb-4">Quick Links</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <a
            href="https://ai-api.windingriversoftware.com/openapi.json"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ExternalLink className="w-4 h-4 text-navy-600" />
            <span className="text-sm text-navy-700">AI API OpenAPI JSON</span>
          </a>
          <a
            href="https://vitejs.dev/guide/env-and-mode.html"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ExternalLink className="w-4 h-4 text-navy-600" />
            <span className="text-sm text-navy-700">Vite Environment Variables</span>
          </a>
          <a
            href="https://developer.mozilla.org/en-US/docs/Web/HTTP/CORS"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ExternalLink className="w-4 h-4 text-navy-600" />
            <span className="text-sm text-navy-700">CORS Documentation</span>
          </a>
          <a
            href="https://supabase.com/docs"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ExternalLink className="w-4 h-4 text-navy-600" />
            <span className="text-sm text-navy-700">Supabase Docs</span>
          </a>
        </div>
      </div>

      {/* Environment Setup */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-navy-900 mb-4">Environment Configuration</h3>
        <p className="text-navy-600 mb-4">
          Create a <code>.env</code> file in your project root with your API configuration:
        </p>
        <div className="bg-navy-50 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-navy-700">.env file content:</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => copyToClipboard('VITE_INVENTORY_API_URL=https://inventory-api.windingriverholdings.com\nVITE_AI_API_URL=https://api.windingriversoftware.com\nVITE_ENABLE_GOOGLE_AUTH=false', 'env')}
              icon={Copy}
              className="text-xs"
            >
              {copiedCommand === 'env' ? 'Copied!' : 'Copy'}
            </Button>
          </div>
          <pre className="text-xs">
            <code>VITE_INVENTORY_API_URL=https://inventory-api.windingriverholdings.com{'\n'}VITE_AI_API_URL=https://ai-api.windingriversoftware.com{'\n'}VITE_ENABLE_GOOGLE_AUTH=false</code>
          </pre>
        </div>
        <p className="text-xs text-navy-500 mt-2">
          These are the configured API endpoints for your inventory and AI services
        </p>
      </div>
    </div>
  );
};